import axios from 'axios'
import { userManager } from './userManager'

/**
 * Çıkış bildirimi (094 — sızma testi bulgusu BGVL-137400, contracts/auth-logout-api.md §3).
 *
 * <p>Kurumsal çıkış yönlendirmesinden **önce** sunucuya "bu oturum kapandı" der. Sunucu, belirtecin
 * oturum kimliğini ve düzenlenme anını kaydeder; o andan sonra aynı oturuma ait **daha eski**
 * belirteçler reddedilir. Çıkışta tarayıcıdaki kopyayı silmek yetmiyordu: belirteç çıkıştan önce
 * kopyalanmışsa süresi dolana kadar kabul edilmeye devam ediyordu.
 *
 * <p><b>Neden ortak `apiClient` DEĞİL:</b> ortak istemcinin 401 köprüsü çıkış sırasında "oturumunuz
 * sona erdi" toast'ı basardı — kullanıcı **çıkarken hata mesajı** görürdü. Bu ucun `Accept-Language`'a
 * da, vekalet başlığına da ihtiyacı yok: gövde döndürmüyor ve kapatılacak oturumu **yalnız
 * belirteçten** çözüyor.
 *
 * <p><b>Neden bekleniyor</b> (gönder-ve-unut değil): hemen ardından `signoutRedirect()` sayfadan
 * ayrılıyor ve tarayıcı uçuştaki isteği **iptal edebilir**. İptal edilirse kayıt hiç oluşmaz, yani
 * koruma **sessizce çalışmaz** — güvenlikte en kötü durum budur.
 *
 * <p><b>Neden sonlu bekleniyor:</b> kullanıcı her koşulda çıkabilmeli (FR-005). Sunucu yanıt vermezse
 * {@link LOGOUT_NOTIFY_TIMEOUT_MS} dolar, istek düşer ve çıkış akışı devam eder.
 */

/** Bekleme üst sınırı. Normal koşulda yanıt milisaniyelerle ölçülür; bu değer yalnız arıza içindir. */
export const LOGOUT_NOTIFY_TIMEOUT_MS = 2000

const logoutClient = axios.create({
  // `||` (`??` DEĞİL) — bkz. `api/client.ts` üstündeki gerekçe: boş string de `??`'yi atlatıp
  // baseURL'i '' yapabiliyor (2026-08-15, rdc dev'de tüm API çağrılarını bozan bug).
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  timeout: LOGOUT_NOTIFY_TIMEOUT_MS,
})

/** Bildirim gönderilecek belirteci döndürür; oturum kaydı yoksa `null`. */
async function tokenToNotifyWith(): Promise<string | null> {
  const user = await userManager.getUser()
  return user?.access_token ?? null
}

/**
 * Oturumun kapandığını sunucuya bildirir.
 *
 * **Hiçbir koşulda hata fırlatmaz.** Ağ hatası, zaman aşımı ve 401 dahil her sonuç yutulur — çağıran
 * (`useLogout`) çıkış yönlendirmesini bu çağrının sonucuna bakmadan yapar.
 */
export async function notifyLogout(): Promise<void> {
  try {
    const token = await tokenToNotifyWith()
    if (!token) {
      // Belirteç yok — kapatılacak bir oturum da yok.
      return
    }
    const response = await logoutClient.post('/auth/logout', undefined, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (response.status !== 204) {
      // Hata YUTULMAYA devam eder (çıkış engellenmez), ama sessiz kalmaz. Somut tuzak: `VITE_API_URL`
      // boş kalırsa istek nginx'in SPA fallback'ine düşer ve `200 + index.html` döner — 2026-08-15'te
      // bu repoda gerçekten yaşandı (bkz. `api/client.ts` başındaki gerekçe ve `assertJsonResponse`).
      // O durumda hiçbir kayıt oluşmaz ve koruma SESSİZCE çalışmaz; en azından iz kalsın.
      console.warn(
        `Çıkış bildirimi beklenmedik yanıt döndü (${response.status}); ` +
          'kapatılmış oturum kaydı oluşmamış olabilir.',
      )
    }
  } catch (error) {
    // Sonuç yine YUTULUR (FR-005): kullanıcı çıkışta çıkmazda bırakılmaz. Ama sessiz kalınmaz —
    // yukarıdaki 204 kontrolü yalnız 2xx'te çalışır, çünkü axios varsayılanı 2xx dışını REDDEDER.
    // Yani "kayıt oluşmadı" ailesinin en tipik üyeleri (404: yol yanlış, 5xx: sunucu yazamadı, zaman
    // aşımı) o kontrolü hiç görmeden buraya düşer ve iz bırakmadan kaybolurdu.
    // 401 hariç tutulur: belirteç zaten geçersizse kapatılacak bir oturum da yoktur, bu beklenen
    // sonuçtur ve her çıkışta gürültü üretmemeli.
    // Metin bilinçli olarak "gönderilemedi" DEMİYOR: bu blok isteğin kendisi kadar `tokenToNotifyWith()`
    // hatalarını da (ör. bozuk depo, private mode) yakalar; o durumda istek hiç atılmamıştır ve
    // "gönderilemedi" teşhiste yanlış yöne bakılmasına yol açar.
    const status = (error as { response?: { status?: number } } | null)?.response?.status
    if (status !== 401) {
      console.warn(
        'Çıkış bildirimi tamamlanamadı; kapatılmış oturum kaydı oluşmamış olabilir.',
        status ?? '(yanıt alınamadı)',
      )
    }
  }
}
